import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Lock, ArrowRight } from "lucide-react";
import api, { apiError, publicFileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const PROPERTY_IMG =
  "https://images.unsplash.com/photo-1708910879696-039ea6515675?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ brand_name: "Startup Agency", logo: "", hero_paragraph: "" });
  useEffect(() => { api.get("/public-settings").then((r) => setBrand(r.data)).catch(() => {}); }, []);

  const forgot = async () => {
    const email = window.prompt("Enter your account email to receive a password reset link:");
    if (!email) return;
    try { await api.post("/auth/forgot-password", { email }); toast.success("If that email exists, a reset link has been sent."); }
    catch (err) { toast.error(apiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      login(data.token, data.user);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left */}
      <div className="noise-bg flex flex-col justify-between p-8 lg:p-14 bg-background">
        <div className="flex items-center gap-2">
          {brand.logo
            ? <img src={publicFileUrl(brand.logo, brand.branding_rev)} alt={brand.brand_name} data-testid="brand-logo" className="h-10 max-w-[180px] object-contain" />
            : (<>
                <div className="h-9 w-9 rounded-md bg-[#18181A] flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <span className="font-head text-xl font-extrabold tracking-tight">{brand.brand_name || "Startup Agency"}</span>
              </>)}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md"
        >
          <p className="overline text-accent mb-4">{brand.hero_overline || "Onboarding & Property CRM"}</p>
          <h1 className="font-head text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] whitespace-pre-line">
            {brand.hero_title || "Nothing goes live\nuntil it's ready."}
          </h1>
          <p className="mt-5 text-muted-foreground text-base leading-relaxed" data-testid="hero-paragraph">
            {brand.hero_paragraph || "The gatekeeper for compliant, market-ready lettings. Track every certificate, signature and key handover from draft to live."}
          </p>

          <form onSubmit={submit} className="mt-9 space-y-4">
            <div>
              <Label className="overline">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                className="mt-1.5 bg-white"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="overline">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                className="mt-1.5 bg-white"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 group"
            >
              {loading ? "Please wait…" : "Sign in"}
              <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <div data-testid="owner-cta-card" className="mt-8 rounded-md border border-[#C6A87C]/40 bg-[#C6A87C]/10 p-5">
            <p className="font-head text-lg font-semibold text-[#18181A]">{brand.owner_cta_text || "Own a holiday property? Let us manage it."}</p>
            <p className="text-sm text-muted-foreground mt-1">Fully managed holiday lets — from compliance to cleaning. Register your interest in a couple of minutes.</p>
            <Link to="/owner-interest" data-testid="owner-interest-cta">
              <Button type="button" variant="outline" className="mt-3 group bg-white">
                Register your property
                <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>

          <button type="button" data-testid="forgot-password-link" onClick={forgot} className="mt-5 block text-sm text-muted-foreground hover:text-foreground transition-colors">
            Forgot password?
          </button>
          <Link to="/register" data-testid="landlord-register-link" className="mt-2 block text-sm text-accent hover:underline">
            Already an owner with an account? Register here →
          </Link>
        </motion.div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" /> {brand.footer_text || "Secured internal access"}
        </div>
      </div>

      {/* Right */}
      <div className="hidden lg:block relative">
        <img src={brand.hero_image ? publicFileUrl(brand.hero_image, brand.branding_rev) : PROPERTY_IMG} alt="Property" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#18181A]/20" />
        <div className="absolute bottom-10 left-10 right-10 bg-white/85 backdrop-blur-md rounded-md p-6 border border-white/40">
          <p className="overline text-accent mb-1">{brand.hero_badge || "Gatekeeper"}</p>
          <p className="font-head text-lg font-semibold text-[#18181A]">
            {brand.hero_caption || "Gas Safety · EICR · EPC · Management Agreement — verified before a listing ever reaches the market."}
          </p>
        </div>
      </div>
    </div>
  );
}
